<template>
  <ErrorBoundary module-name="TheHeader">
    <header class="header">
      <div
        class="header__container"
        :class="classHeaderContainer"
      >
        <transition
          name="switch-search"
          mode="out-in"
        >
          <div
            v-if="!searchActive"
            class="header__arrow-burger-wrapp"
          >
            <HeaderBurger
              v-click-outside="onClickOutside"
              :burger-active="burgerActive"
              class="header__burger"
              @leave="toggleBurger(false)"
              @toggle-burger-menu="toggleBurger(!burgerActive)"
            />

            <ArrowMenu
              class="header__arrow-menu hide<big-tablet"
              :items="mainArrowMenu.items"
              :theme="themeArrowMenuFirstLevel"
              @click-menu-item="onItemMenuFirstLevelClick"
            />

            <div class="header__actions-container">
              <div class="header-actions">
                <div class="header__search">
                  <BaseButton
                    aria-label="Открыть поиск статей"
                    class="header__search--btn"
                    @click="openSearch"
                  >
                    <SvgAsset
                      class="header__search--btn-icon"
                      :content="searchSvg"
                    />
                  </BaseButton>
                </div>
                <HeaderProfile
                  class="header__profile"
                  :class="{ 'header__profile--search-active': searchActive }"
                />
              </div>
            </div>
          </div>

          <HeaderSearch
            v-else
            class="header__header-search"
            @close-search="closeSearch(true)"
            @search="closeSearch"
          />
        </transition>
      </div>
      <!-- Скрытый инпут для клавиатуры на ios -->
      <input
        ref="hiddenInput"
        class="hidden-input"
        tabIndex="-1"
        aria-hidden="true"
      />
    </header>
  </ErrorBoundary>
</template>

<script lang="ts">
  import { vOnClickOutside } from '@vueuse/components';
  import HeaderBurger from '~/components/TheHeader/Burger/HeaderBurger.vue';
  import HeaderProfile from '~/components/TheHeader/Profile/HeaderProfile.vue';
  import BaseButton from '~/components/BaseComponents/BaseButton.vue';
  import ArrowMenu from '~/components/ArrowMenu.vue';
  import HeaderSearch from '~/components/TheHeader/Search/HeaderSearch.vue';
  import getSlugFromUrl from '~/utils/getSlugFromUrl';
  import MobileSearch from '~/components/MobileSearch.vue';
  import type { ILink } from '~/components/ArrowMenu/ArrowMenu';
  import { useModal } from '@devhacker/shared/plugins/modal/useModal';
  import { useMenusStore } from '~/store/menus';
  import { useWindowStore } from '~/store/window';
  import SvgAsset from '@devhacker/shared/components/SvgAsset.vue';
  import searchSvg from '~/assets/images/svg/search.svg?raw';
  import ErrorBoundary from '@devhacker/shared/components/ErrorBoundary.vue';

  export default defineNuxtComponent({
    name: 'TheHeader',

    components: {
      HeaderProfile,
      HeaderBurger,
      BaseButton,
      ArrowMenu,
      HeaderSearch,
      SvgAsset,
      ErrorBoundary,
    },

    directives: {
      clickOutside: vOnClickOutside,
    },

    // для отправки аналитики
    provide() {
      return {
        eventCategory: () => 'Хедер',
      };
    },

    data() {
      return {
        burgerActive: false,
        timeout: 0,
        searchActive: false,
      };
    },

    computed: {
      searchSvg: () => searchSvg,

      mainArrowMenu() {
        return useMenusStore().mainArrowMenu;
      },

      isMobile() {
        return useWindowStore().isMobile;
      },

      themeArrowMenuFirstLevel(): string | null | undefined {
        return useRoute().name === 'preview' ? useRoute().name : 'light';
      },

      classHeaderContainer(): Record<string, boolean | undefined> {
        return {
          [`header__container--${useRoute().name?.toString()}`]: true,
        };
      },

      currentSlug(): string {
        return getSlugFromUrl(useRoute().fullPath);
      },
    },

    methods: {
      onItemMenuFirstLevelClick(menuItem: ILink) {
        this.sendAnalytics(menuItem);
      },

      // Функция для аналитики клика на элемент меню хедера
      sendAnalytics(menuItem: ILink) {
        this.$sendYandexMetrika({
          level1: 'Клик_Хедер',
          level4: menuItem.link,
          level5: menuItem.title,
          level6: '1',
        });
      },

      toggleBurger(burgerState: boolean): void {
        clearTimeout(this.timeout);
        this.burgerActive = burgerState;
      },

      closeOnLeave(): void {
        this.timeout = window.setTimeout(() => {
          this.burgerActive = false;
        }, 1000);
      },

      openSearch(): void {
        this.$sendYandexMetrika({
          level1: 'Событие_Поиск',
          level6: 'Открытие поиска',
        });

        // На ios не появляется клавиатура при ассинхронном фокусе
        // Внутри transition инпута поиска сразу не существует
        // Потому делаем фокус на фейковый скрытый инпут для появления клавиатуры
        // После фокус переходит на инпут поиска и клавиатура остается
        (this.$refs.hiddenInput as HTMLInputElement | undefined)?.focus();

        if (this.isMobile) {
          useModal().open(
            MobileSearch,
            {
              closeClickOutside: true,
            },
            {
              transition: 'modal-fade',
            },
          );
        } else {
          this.searchActive = true;
        }
      },

      closeSearch(clickOnExit: boolean): void {
        if (this.isMobile) {
          useModal().close();
        } else {
          this.searchActive = false;
        }

        if (clickOnExit) {
          this.$sendYandexMetrika({
            level1: 'Событие_Поиск',
            level6: 'Закрытие поиска',
          });
        }
      },

      onClickOutside(): void {
        this.burgerActive = false;
      },
    },
  });
</script>

<style lang="scss" scoped>
  @use '~/components/TheHeader/styles/TheHeader.variables.scss' as *;
  @use '~/components/TheHeader/styles/TheHeader.animations.scss' as *;
  @use 'sass:color';

  @include search-fade;

  .hidden-input {
    display: block;
    width: 0;
    height: 0;
  }

  .header {
    font-family: $text-font--1;
    background-color: $main-header-bg;

    &__container-wrapp {
      position: relative;
    }

    &__container {
      position: relative;
      z-index: 7;
      margin: 0 auto;

      &--preview {
        background-color: color.adjust($hover-color--2, $lightness: 30%);
      }
    }

    &__arrow-burger-wrapp {
      display: flex;
      justify-content: space-between;
      align-items: center;
      max-width: 1280px;
      margin: 0 auto;
    }

    &__burger {
      @include tablet {
        flex-basis: 320px;
      }
    }

    // повышаем специфичность, т.к .arrow-menu перебивает display
    .header &__arrow-menu {
      height: $headerHeight;
      max-width: 870px;
      width: fit-content;
      margin: 0 auto;
    }

    &__actions-container {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      width: fit-content;

      .header-actions {
        z-index: 2;

        display: flex;
        justify-content: space-between;
        align-items: center;
      }
    }

    &__header-search {
      width: 100%;
      max-width: 1280px;
      margin: 0 auto;
    }

    &__search {
      @include hover {
        background: $headerHoverItemColor;
      }

      height: 48px;
      width: 48px;
      display: flex;
      align-items: center;
      justify-content: center;

      &--btn {
        width: 24px;
        height: 24px;
        transition: position 0.1s ease-in 0.3s;

        &-icon {
          color: $headerSearchIconColor;
        }
      }
    }

    &__profile {
      height: 48px;
      width: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1;
      margin-left: auto;
      @include hover {
        background-color: $headerHoverItemColor;
      }

      &--search-active {
        margin-left: 172px;
      }
    }
  }
</style>
